import { useRef, useState } from 'react';
import { requestableRoleForCapability, type RequestableSchool } from '../../lib/types/accessRequest';
import type { Capability } from '../../lib/types/auth';
import { Button } from '../ui/Button';
import { RequestAccessDialog } from './RequestAccessDialog';

const SUPPORT_MAILTO =
  'mailto:itservicedesk@aurora-group.invalid?subject=UCP%20access%20request';

/**
 * Contextual "you don't have access — request it" affordance (PRO-0580 / ADR-036,
 * AC-2). Shown where a caller hits a `403 forbidden` on a capability-gated surface.
 * It reverse-maps the denied `capability` to a self-requestable role (CF-SEC-001):
 *   - a mapped role → offer a one-click `RequestAccessDialog`, pre-filled with the
 *     role + the current tenant school (no picker);
 *   - `null` (appointment-only, e.g. `complaint_oversight_read` → OPS_DIRECTOR) →
 *     show the IT Service Desk contact fallback, because offering a request the
 *     server would reject would be a dead-end.
 *
 * When the feature flag is off (`enabled={false}`) it renders nothing but the
 * contact fallback, so the shipped behaviour is unchanged.
 */

interface ForbiddenRequestAffordanceProps {
  /** The capability the caller was denied. */
  capability: Capability;
  /** The current tenant school (the request's scope on the 403 path). */
  school: RequestableSchool;
  /** `featureFlags.accessRequestEnabled` — gates the in-app request affordance. */
  enabled: boolean;
}

export function ForbiddenRequestAffordance({
  capability,
  school,
  enabled,
}: ForbiddenRequestAffordanceProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const requestableRole = requestableRoleForCapability(capability);

  const contactFallback = (
    <p className="text-sm text-text-secondary">
      To request access, contact the IT Service Desk:{' '}
      <a href={SUPPORT_MAILTO}>itservicedesk@aurora-group</a>.
    </p>
  );

  if (!enabled || requestableRole === null) {
    return contactFallback;
  }

  return (
    <>
      <Button ref={triggerRef} variant="primary" onClick={() => setOpen(true)}>
        Request access
      </Button>
      <RequestAccessDialog
        open={open}
        onClose={() => setOpen(false)}
        returnFocusRef={triggerRef}
        prefilledRole={requestableRole}
        fixedSchool={school}
      />
    </>
  );
}
